<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    class="tw:flex tw:flex-col tw:gap-4"
    data-testid="outreach-contact-profile"
  >
    <div
      class="tw:flex tw:flex-col"
      :class="[
        ...contactClass,
        {
          'tw:border tw:border-[var(--s-color-denim-2)] tw:rounded-md':
            outlined,
        },
      ]"
    >
      <OutreachContactProfileSection
        v-if="contactProfile"
        v-model="showAll.bio"
      >
        <template #title>
          {{ getLocaleText("outreach_contact_profile.bio") }}
        </template>
        <div v-html="contactProfile" />
      </OutreachContactProfileSection>

      <template v-if="type === 'influencer' && hasStats">
        <OutreachContactProfileSection
          v-for="stat in statKeys"
          :key="stat"
        >
          <template #title>
            <OutreachContactStatLabel
              class="tw:underline tw:decoration-dotted tw:leading-[1.5]"
              :contact="contact"
              :type="stat"
            />
          </template>

          <template #content>
            <OutreachContactStat
              :contact="contact"
              :type="stat"
            />
          </template>
        </OutreachContactProfileSection>
      </template>

      <OutreachContactProfileSection
        v-if="contactLanguages"
        v-model="showAll.language"
      >
        <template #title>
          {{ getLocaleText("outreach_contact_profile.language") }}
        </template>
        {{ contactLanguages }}
      </OutreachContactProfileSection>

      <OutreachContactProfileSection
        v-if="isOutletContact(contact) && contact.owner"
        v-model="showAll.publisher"
      >
        <template #title>
          {{ getLocaleText("outreach_contact_profile.publisher") }}
        </template>
        {{ contact.owner }}
      </OutreachContactProfileSection>

      <OutreachContactProfileSection
        v-if="visibleContactAddresses.length"
        v-model="showAll.address"
      >
        <template #title>
          {{ getLocaleText("outreach_contact_profile.address") }}
        </template>
        <div
          v-for="(address, addressIndex) in visibleContactAddresses"
          :key="address.id"
          :class="{ 'q-mt-md': addressIndex }"
        >
          {{ address.text }}
        </div>
      </OutreachContactProfileSection>

      <OutreachContactProfileSection
        v-if="contactPhones.length"
        v-model="showAll.phone"
      >
        <template #title>
          {{ getLocaleText("outreach_contact_details.phone_label") }}
        </template>
        <div
          v-for="(phone, phoneIndex) in visibleContactPhones"
          :key="`phone_${phoneIndex}`"
          :class="{ 'q-mt-md': phoneIndex }"
        >
          {{ phone }}
        </div>
      </OutreachContactProfileSection>

      <OutreachContactProfileSection
        v-if="contactFrequency"
        v-model="showAll.frequency"
      >
        <template #title>
          {{ getLocaleText("outreach_contact_profile.frequency") }}
        </template>
        {{ contactFrequency }}
      </OutreachContactProfileSection>

      <template
        v-for="circulation in contactCirculations"
        :key="circulation.circulationType"
      >
        <OutreachContactProfileSection
          v-if="String(circulation.circulation).match(/^[0-9+.,]+$/)"
          v-model="showAll.circulation"
        >
          <template #title>
            <div class="tw:flex tw:items-center tw:gap-1">
              {{
                getLocaleText(
                  `outreach_contact_profile.${circulation.circulationType}`
                )
              }}
              <QIcon
                v-if="
                  ['uvpm_total', 'circulation'].includes(
                    circulation.circulationType
                  )
                "
                name="info"
              >
                <BaseStreemTooltip>
                  {{
                    getLocaleText(
                      `outreach_contact_profile.${circulation.circulationType}_tooltip`
                    )
                  }}
                </BaseStreemTooltip>
              </QIcon>
            </div>
          </template>
          {{ prettyNumber(circulation.circulation) }}
        </OutreachContactProfileSection>
      </template>

      <OutreachContactProfileSection
        v-if="contactDmaNames"
        v-model="showAll.dma"
      >
        <template #title>
          {{ getLocaleText("outreach_contact_profile.dma") }}
        </template>

        {{ contactDmaNames }}
      </OutreachContactProfileSection>

      <OutreachContactProfileSection
        v-if="isInfluencerContact(contact) && contactOutlets.length"
        v-model="showAll.outlets"
      >
        <template #title>
          {{ getLocaleText("outreach_contact_profile.outlets") }}
        </template>

        <OutreachContactListItems
          clickable
          :items="contactOutlets"
          show-all
          @click="onClickOutlet"
        />
      </OutreachContactProfileSection>

      <OutreachContactProfileSection
        v-if="contactTopics.length"
        v-model="showAll.sectors"
      >
        <template #title>
          {{ getLocaleText("outreach_contact_profile.sectors") }}
        </template>

        <OutreachContactListItems
          clickable
          :items="contactTopics"
          show-all
          @click="onClickTopic"
        />
      </OutreachContactProfileSection>

      <OutreachContactProfileSection
        :class="{
          'feature-opaque': outreachFeatures.contactPrivateData().isDisabled,
          'tw:border-b-0': outlined,
        }"
      >
        <template #title>
          {{ getLocaleText("outreach_contact_profile.private_data") }}
        </template>

        <template #content>
          <div class="tw:flex tw:flex-col tw:gap-4">
            <div
              v-if="contactDetailsFormatted.length"
              class="tw:flex tw:flex-col tw:gap-4 tw:leading-[1.5]"
            >
              <QSpinnerDots
                v-if="loadingContactDetails"
                size="20px"
              />
              <OutreachContactProfileContent
                v-for="(item, index) in contactDetailsFormatted"
                v-else
                :key="`contact-details-${index}`"
              >
                <span :class="item.class">
                  <span class="row items-center">
                    {{ item.label }}
                    <QIcon
                      v-if="item.icon"
                      class="q-ml-sm"
                      :name="item.icon"
                    />
                  </span>

                  <BaseStreemTooltip v-if="item.tooltip">
                    {{ item.tooltip }}
                  </BaseStreemTooltip>
                </span>
              </OutreachContactProfileContent>
            </div>

            <BaseButton
              class="tw:items-start tw:font-medium"
              icon="far fa-edit"
              icon-size="sm"
              :label="
                getLocaleText('outreach_contact_profile.add_private_data')
              "
              new-design
              style="max-width: fit-content"
              @click="openContactDetailsModal"
            />
          </div>
        </template>
      </OutreachContactProfileSection>
    </div>

    <div class="tw:flex tw:justify-end">
      <BaseButton
        bordered
        :disabled="outreachFeatures.activities().isDisabled"
        icon="fas fa-history"
        icon-size="sm"
        :label="$t('outreach_contact_profile.update_label')"
        new-design
        @click="openChangeRequestModal"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import mapValues from "lodash/mapValues";
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { outreachFeatures } from "shared/boot/authorization";
import { getLocaleText } from "shared/boot/i18n";
import { BaseButton } from "shared/components/base";
import BaseStreemTooltip from "shared/components/base/BaseStreemTooltip";
import OutreachContactListItems from "shared/components/Outreach/OutreachContact/OutreachContactListItems.vue";
import OutreachContactStat from "shared/components/Outreach/OutreachContact/OutreachContactStat.vue";
import OutreachContactStatLabel from "shared/components/Outreach/OutreachContact/OutreachContactStatLabel.vue";
import useContact, {
  type ContactType,
  isInfluencerContact,
  isOutletContact,
} from "shared/composables/useContact";
import { useOutreachContactDetails } from "shared/composables/useOutreachContactDetails";
import {
  type OutreachContactStatKey,
  useOutreachContactSocialStats,
} from "shared/composables/useOutreachContactSocialStats";
import { prettyNumber } from "shared/helpers/number";
import { safePush } from "shared/helpers/routing";
import ModalService from "shared/modals/ModalService";
import { useOutreachContactsStore } from "shared/stores/outreachContacts";
import { type OutreachTopic } from "shared/types/outreach";

import OutreachContactProfileContent from "./OutreachContactProfileContent.vue";
import OutreachContactProfileSection from "./OutreachContactProfileSection.vue";

const props = defineProps<{
  contact: ContactType;
  outlined?: boolean;
  skipPrivateData?: boolean;
}>();

interface OutreachContactDetail {
  class?: string;
  icon?: string;
  label: string;
  tooltip?: string;
}

const {
  contactCirculations,
  contactClass,
  contactDmaNames,
  contactFrequency,
  contactLanguages,
  contactOutlets,
  contactPhones,
  contactProfile,
  contactTopics,
  showAllAddresses,
  type,
  visibleContactAddresses,
  visibleContactPhones,
} = useContact(props);

const router = useRouter();
const safeRouterPush = safePush({ router });

const { stats, hasStats } = useOutreachContactSocialStats(props);

const { contactDetails, loadingContactDetails, loadContactDetails } =
  useOutreachContactDetails({
    contactId: props.contact.id,
    contactType: type.value,
  });

const statKeys = computed<OutreachContactStatKey[]>(
  () => Object.keys(stats.value) as OutreachContactStatKey[]
);

const { setFilterTopics } = useOutreachContactsStore();

showAllAddresses.value = true;

const showAll = ref<Record<string, boolean>>({
  bio: false,
  twitterBio: false,
  language: false,
  publisher: false,
  address: false,
  frequency: false,
  circulation: false,
  outlets: false,
  sectors: false,
  phone: false,
  dma: false,
});

const contactDetailsFormatted = computed<OutreachContactDetail[]>(() => {
  const items: OutreachContactDetail[] = [];

  if (contactDetails.value?.note) {
    items.push({ label: contactDetails.value.note });
  }

  if (contactDetails.value && contactDetails.value.email) {
    const emailContact = {
      label: contactDetails.value?.email,
      ...(contactDetails.value?.invalidEmail
        ? {
            class: "text-error",
            tooltip: getLocaleText(
              "outreach_contact_profile.undeliverable_email"
            ),
            icon: "error",
          }
        : {}),
    };

    items.push(emailContact);
  }

  if (contactDetails.value?.phone) {
    items.push({ label: contactDetails.value.phone });
  }

  if (contactDetails.value?.address) {
    const { addressLine1, addressLine2, city, postalCode, state, country } =
      contactDetails.value.address;

    const lines = [
      addressLine1,
      addressLine2,
      city,
      postalCode,
      ...(country !== "United Kingdom" && state ? [state] : []),
      country,
    ].filter(Boolean);

    if (lines.length) {
      items.push({
        label: lines.map((line) => line.replace(/,\s*$/, "")).join(", "),
      });
    }
  }

  return items;
});

function openChangeRequestModal(): void {
  ModalService.open("OutreachChangeRequestModal", {
    props: {
      requestType: type.value,
      contactId: String(props.contact.id),
    },
  });
}

function openContactDetailsModal(): void {
  ModalService.open("OutreachContactDetailsModal", {
    props: {
      contactId: props.contact.id!,
      contactType: type.value,
    },
    events: {
      saved: loadContactDetails,
    },
  });
}

function onClickOutlet(outlet: ContactType) {
  safeRouterPush({
    name: "outreach-search-outlet",
    params: {
      id: outlet.id,
    },
  });
}

function onClickTopic(topic: OutreachTopic) {
  setFilterTopics([topic]);

  safeRouterPush({
    name: "outreach-search",
  });
}

watch(
  () => props.contact,
  () => {
    showAll.value = mapValues(showAll.value, () => false);
  }
);

onMounted(() => {
  if (!props.skipPrivateData) {
    loadContactDetails();
  }
});
</script>

<style lang="scss" scoped>
.feature-opaque {
  opacity: 0.5;
  filter: blur(4px);
  pointer-events: none;
}
</style>
